import {AxiosCall, CancelTokenSource} from '../axios';
import {ServiceSuccessResponseType} from './service-mappers/BaseMapper';
import {ArrayResponse} from './service-mappers/CommonServiceMapper';
import {
  ITicket,
  IWorkflowDetailsResponse,
  IWorkFlowActivityItem,
  IWorkFlow,
  IWorkFlowStatus,
  IModuleTicketFields,
  IPriorAuthTicketDetails,
  IClaimsTicketDetails,
  IWorkflowImportId,
  ITicketingSystemImportSummary,
  ITicketDetailsPortal,
  ITicketStatusCountItem
} from './service-mappers/WorkflowMapper';
import {serializeQueryParams} from './PaymentHistoryService';
import {UserModelMapper} from './service-mappers/ModelMapper';
import {IVisitTypeConfigurationItem} from './service-mappers/PriorAuthorizationMapper';

export const getWorkflowDetails = async ({
  clinicGroupId,
  params
}: {
  clinicGroupId: number;
  params: {module: string};
}): Promise<ServiceSuccessResponseType<IWorkflowDetailsResponse>> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.get(
    `/clinic-group/${clinicGroupId}/ticketing-system/work-flow?${queryParams}`
  );
};

export const getAllTickets = async ({
  clinicGroupId,
  clinicId,
  params,
  cancelToken
}: {
  clinicGroupId: number;
  clinicId: number;
  params: {module: string} & Partial<{
    workflow_id: IWorkFlow['id'];
    workflow_status_id: IWorkFlowStatus['id'];
    patient_name: string;
    patient_id: number;
    limit: number;
    offset: number;
    is_my_tickets: boolean;
    provider_id: number;
    is_open_tickets: boolean;
    is_unassigned_tickets: boolean;
    is_need_clinic_attention: boolean;
    [index: string]: string | number | boolean;
  }>;
  cancelToken?: CancelTokenSource;
}): Promise<ServiceSuccessResponseType<ArrayResponse<ITicket>>> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.get(
    `/clinic-group/${clinicGroupId}/clinic/${clinicId}/ticketing-system/ticket/all?${queryParams}`,
    cancelToken
  );
};

export const getTicketDetails = async ({
  clinicGroupId,
  clinicId,
  ticketId,
  params
}: {
  clinicGroupId: number;
  clinicId: number;
  ticketId: number;
  params: {module: string};
}): Promise<
  ServiceSuccessResponseType<IPriorAuthTicketDetails | IClaimsTicketDetails>
> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.get(
    `/clinic-group/${clinicGroupId}/clinic/${clinicId}/ticketing-system/ticket/${ticketId}?${queryParams}`
  );
};

export const deleteTicket = ({
  clinicGroupId,
  clinicId,
  tickedId,
  params
}: {
  clinicGroupId: number;
  clinicId: number;
  tickedId: number;
  params: {module: string};
}): Promise<Promise<ServiceSuccessResponseType<never>>> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.delete(
    `/clinic-group/${clinicGroupId}/clinic/${clinicId}/ticketing-system/ticket/${tickedId}?${queryParams}`
  );
};

export const createTicket = async ({
  clinicGroupId,
  clinicId,
  payload,
  params
}: {
  clinicGroupId: number;
  clinicId: number;
  payload: {
    patient_id: number;
    title: string;
    priority?: number | null;
    description?: string;
    workflow_id: IWorkFlow['id'];
    assigned_to?: UserModelMapper['id'];
    custom_fields?: Record<string, any>;
  };
  params: {
    module: string;
  };
}): Promise<ServiceSuccessResponseType<{id: ITicket['id']}>> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.post(
    `/clinic-group/${clinicGroupId}/clinic/${clinicId}/ticketing-system/ticket?${queryParams}`,
    payload
  );
};

export const updateTicket = async ({
  clinicGroupId,
  clinicId,
  ticketId,
  payload,
  params
}: {
  clinicGroupId: number;
  clinicId: number;
  ticketId: number;
  payload: {
    title?: string;
    priority?: number;
    description?: string;
    assigned_to?: number;
    workflow_id?: IWorkFlow['id'];
    workflow_status_code?: IWorkFlowStatus['code'];
    workflow_status_id?: IWorkFlowStatus['id'];
    custom_fields?: Record<string, any>;
    is_completed?: boolean;
  };
  params: {module: string};
}): Promise<ServiceSuccessResponseType<never>> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.put(
    `/clinic-group/${clinicGroupId}/clinic/${clinicId}/ticketing-system/ticket/${ticketId}?${queryParams}`,
    payload
  );
};

export const createTicketComment = async ({
  clinicGroupId,
  clinicId,
  tickedId,
  payload,
  params
}: {
  clinicGroupId: number;
  clinicId: number;
  tickedId: number;
  payload: {
    text: string;
    type: string;
  };
  params: {
    module: string;
  };
}): Promise<Promise<ServiceSuccessResponseType<never>>> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.post(
    `/clinic-group/${clinicGroupId}/clinic/${clinicId}/ticketing-system/ticket/${tickedId}/action-history?${queryParams}`,
    payload
  );
};

export const updateTicketComment = async ({
  clinicGroupId,
  clinicId,
  tickedId,
  payload,
  actionId,
  params
}: {
  clinicGroupId: number;
  clinicId: number;
  tickedId: number;
  actionId: number;
  payload: {
    text: string;
    moduleId: string;
  };
  params: {
    module: string;
  };
}): Promise<Promise<ServiceSuccessResponseType<never>>> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.put(
    `/clinic-group/${clinicGroupId}/clinic/${clinicId}/ticketing-system/ticket/${tickedId}/action-history/${actionId}?${queryParams}`,
    payload
  );
};

export const getTicketActivity = ({
  clinicGroupId,
  clinicId,
  tickedId,
  params
}: {
  clinicGroupId: number;
  clinicId: number;
  tickedId: number;
  params: {
    type?: string;
    limit?: number;
    offset?: number;
    module: string;
  };
}): Promise<
  ServiceSuccessResponseType<ArrayResponse<IWorkFlowActivityItem>>
> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.get(
    `/clinic-group/${clinicGroupId}/clinic/${clinicId}/ticketing-system/ticket/${tickedId}/action-history?${queryParams}`
  );
};

export const deleteTicketComment = ({
  clinicGroupId,
  clinicId,
  tickedId,
  actionId,
  params
}: {
  clinicGroupId: number;
  clinicId: number;
  tickedId: number;
  actionId: number;
  params: {
    module: string;
  };
}): Promise<Promise<ServiceSuccessResponseType<never>>> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.delete(
    `/clinic-group/${clinicGroupId}/clinic/${clinicId}/ticketing-system/ticket/${tickedId}/action-history/${actionId}?${queryParams}`
  );
};

export const addTicketAttachment = async ({
  clinicGroupId,
  clinicId,
  patientTicketId,
  data,
  params
}: {
  clinicGroupId: number;
  clinicId: number;
  patientTicketId: number;
  data: any;
  params: {
    module: string;
  };
}): Promise<Promise<ServiceSuccessResponseType<never>>> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.post(
    `/clinic-group/${clinicGroupId}/clinic/${clinicId}/ticketing-system/ticket/${patientTicketId}/attachment?${queryParams}`,
    data
  );
};

export const getTicketAttachments = ({
  clinicGroupId,
  clinicId,
  patientTicketId,
  attachmentId,
  params
}: {
  clinicGroupId: number;
  clinicId: number;
  patientTicketId: number;
  attachmentId: number | string;
  params: {module: string};
}): Promise<
  ServiceSuccessResponseType<ArrayResponse<IWorkFlowActivityItem>>
> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  const url = `/clinic-group/${clinicGroupId}/clinic/${clinicId}/ticketing-system/ticket/${patientTicketId}/attachment/${attachmentId}?${queryParams}`;
  return AxiosCall.getImage(url, {responseType: 'arraybuffer'});
};

// Prior Auth Timer Service Functions

export const startTicketTimer = async (
  clinic_group_id: number,
  clinic_id: number,
  patient_ticket_id: number,
  payload: {
    start_time: string;
  },
  params: {module: string}
): Promise<ServiceSuccessResponseType<never>> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/ticketing-system/ticket/${patient_ticket_id}/work-log/start-timer?${queryParams}`,
    payload
  );
};

export const stopTicketTimer = async (
  clinic_group_id: number,
  clinic_id: number,
  patient_ticket_id: number,
  log_id: number | string | null,
  payload: {
    stop_time: string;
    notes: string;
  },
  params: {module: string}
): Promise<any> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/ticketing-system/ticket/${patient_ticket_id}/work-log/${log_id}/stop-timer?${queryParams}`,
    payload
  );
};

export const getTicketWorkLogs = async (
  clinic_group_id: number,
  clinic_id: number,
  patient_ticket_id: number,
  params: {
    limit?: number;
    offset?: number;
    module: string;
  }
): Promise<any> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/ticketing-system/ticket/${patient_ticket_id}/work-log?${queryParams}`
  );
};

export const getVisitTypeAndProcedureCodes = async (
  clinic_group_id: number,
  options?: {
    limit?: number;
    offset?: number;
  }
): Promise<
  ServiceSuccessResponseType<ArrayResponse<IVisitTypeConfigurationItem>>
> => {
  let queryParams = '';
  if (options) {
    queryParams = serializeQueryParams(options);
  }
  return AxiosCall.get(
    `clinic-group/${clinic_group_id}/prior-authorization/visit-type-config?${queryParams}`
  );
};

export const createVisitTypeConfigurations = async ({
  clinic_group_id,
  data
}: {
  clinic_group_id: number;
  data: any;
}): Promise<ServiceSuccessResponseType<void>> => {
  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/prior-authorization/visit-type-config`,
    data
  );
};

export const updateVisitTypeConfiguration = async ({
  clinic_group_id,
  data,
  visit_type_id
}: {
  clinic_group_id: number;
  data: any;
  visit_type_id: number;
}): Promise<ServiceSuccessResponseType<void>> => {
  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/prior-authorization/visit-type-config/${visit_type_id}`,
    data
  );
};

export const deleteVisitTypeConfiguration = async ({
  clinic_group_id,
  visit_type_id
}: {
  clinic_group_id: number;
  visit_type_id: number;
}): Promise<ServiceSuccessResponseType<void>> => {
  return AxiosCall.delete(
    `/clinic-group/${clinic_group_id}/prior-authorization/visit-type-config/${visit_type_id}`
  );
};

export const saveProcedureCodeMappings = async (
  clinic_group_id: number | null,
  data: any
): Promise<any> => {
  return AxiosCall.post(
    `clinic-group/${clinic_group_id}/prior-authorization/import-procedure-types`,
    data
  );
};
export const addWorkLogTime = async (
  clinic_group_id: number,
  clinic_id: number,
  patient_ticket_id: number,
  payload: {
    start_time: string;
    stop_time: string;
    time_spent: string;
    notes: string;
  },
  params: {
    module: string;
  }
): Promise<any> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.post(
    `clinic-group/${clinic_group_id}/clinic/${clinic_id}/ticketing-system/ticket/${patient_ticket_id}/work-log/add-time?${queryParams}`,
    payload
  );
};

export const getModuleTicketFields = ({
  clinic_group_id,
  params
}: {
  clinic_group_id: number;
  params: {
    module: string;
  };
}): Promise<ServiceSuccessResponseType<IModuleTicketFields[]>> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/ticketing-system/fields?${queryParams}`
  );
};

export const bulkAssignTicketsToUser = ({
  clinic_group_id,
  clinic_id,
  payload,
  params
}: {
  clinic_group_id: number;
  clinic_id: number;
  params: {
    module: string;
  };
  payload: {
    workflow_status_id?: IWorkFlow['id'];
    patient_name?: string;
    patient_id?: number;
    provider_id?: number;
    assigned_to?: number;
    is_open_tickets?: boolean;
    is_unassigned_tickets?: boolean;
    is_unattended_ticket?: boolean;
    filter_by?: string;
    min_age?: number;
    max_age?: number;
    assigned_to_user_id: number | string;
    ticket_ids?: ITicket['id'][];
    [index: string]: string | number | boolean | number[] | undefined;
    priority_setting?: number;
  };
}): Promise<Promise<ServiceSuccessResponseType<never>>> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/ticketing-system/ticket/bulk-assign?${queryParams}`,
    payload
  );
};
export const downloadTemplate = async ({
  type,
  params
}: {
  type: string;
  params?: {
    clinic_group_id?: number;
    clinic_id?: number;
  };
}): Promise<any> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.get(`/file/template/${type}?${queryParams}`);
};

export const saveBulkData = async ({
  clinic_group_id,
  clinic_id,
  data,
  params
}: {
  clinic_group_id: number | null;
  clinic_id: number;
  data: any;
  params: {
    module: string;
  };
}): Promise<any> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.post(
    `clinic-group/${clinic_group_id}/clinic/${clinic_id}/ticketing-system/ticket/import?${queryParams}`,
    data
  );
};

export const getDashboardMetrics = async ({
  clinic_group_id,
  clinic_id,
  params
}: {
  clinic_group_id: number;
  clinic_id: number;
  params: any;
}): Promise<any> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.get(
    `clinic-group/${clinic_group_id}/clinic/${clinic_id}/ticketing-system/metrics?${queryParams}`
  );
};

export const getPreviousImportIds = async ({
  clinicGroupId,
  clinicId,
  params
}: {
  clinicGroupId: number;
  clinicId: number;
  params: {
    module: string;
    limit?: number;
    offset?: number;
  };
}): Promise<ServiceSuccessResponseType<ArrayResponse<IWorkflowImportId>>> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.get(
    `/clinic-group/${clinicGroupId}/clinic/${clinicId}/ticketing-system/import/summary/import-id?${queryParams}`
  );
};

export const getPrevImportSummary = ({
  clinicGroupId,
  clinicId,
  params
}: {
  clinicGroupId: number;
  clinicId: number;
  params: {
    module: string;
    limit?: number;
    offset?: number;
    import_id: string;
  };
}): Promise<ServiceSuccessResponseType<ITicketingSystemImportSummary>> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.get(
    `/clinic-group/${clinicGroupId}/clinic/${clinicId}/ticketing-system/import/summary?${queryParams}`
  );
};

export const getPatientTicketInPortal = ({
  params,
  patientId
}: {
  patientId: number;
  params: {
    module: string;
    clinic_id?: number;
    active_prior_auth?: boolean;
  };
}): Promise<
  ServiceSuccessResponseType<ArrayResponse<ITicketDetailsPortal>>
> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.get(
    `/guarantor/ticketing-system/patient/${patientId}/ticket/all?${queryParams}`
  );
};

export const sendNeedAttentionNotifications = async ({
  clinic_group_id,
  clinic_id,
  emails,
  params
}: {
  clinic_group_id: number | null;
  clinic_id: number;
  emails: any;
  params: {
    module: string;
  };
}): Promise<any> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.post(
    `clinic-group/${clinic_group_id}/clinic/${clinic_id}/prior-authorization/need_attention?${queryParams}`,
    {emails}
  );
};
export const getStatusCount = ({
  clinicGroupId,
  clinicId,
  params
}: {
  clinicGroupId: number;
  clinicId: number;
  params: {
    module: string;
  };
}): Promise<
  ServiceSuccessResponseType<ArrayResponse<ITicketStatusCountItem>>
> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.get(
    `/clinic-group/${clinicGroupId}/clinic/${clinicId}/ticketing-system/status-count?${queryParams}`
  );
};
